import { HttpClient } from '@angular/common/http';
import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { TRIO_WIDGET_REGISTRY,  TRIO_WIDGET_REGISTRY_PUBLISH } from '@unx/theme-2';
import { LbwBrandSettingsModel, UnxApiResponse } from '@unx/wdk/shared/model';
import { IUnxBrandSettingsService } from '@unx/wdk/shared/services';
import { BRAND_SETTINGS_TOKEN } from '@unx/wdk/shared/tokens';
import { resolveBrandVisualUrl } from '@unx/wdk/shared/utils';
import { distinctUntilChanged, Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LobuBrandSettingsService implements IUnxBrandSettingsService {
    private _settings: LbwBrandSettingsModel = {} as LbwBrandSettingsModel;

    constructor(
        private _httpClient: HttpClient,
        @Inject(BRAND_SETTINGS_TOKEN) brandSettings: LbwBrandSettingsModel,
        @Inject(DOCUMENT) private _document: Document,
        private _transloco: TranslocoService,
    ) {
        this._settings = brandSettings;
        this._settings.widgetRegistry = TRIO_WIDGET_REGISTRY_PUBLISH;
        this._applyAuthGameVisualCss();
        this._applyFaviconLink();
        this._transloco.langChanges$
            .pipe(distinctUntilChanged())
            .subscribe(() => this._applyAuthGameVisualCss());
    }

    getBrandSettings(): Observable<UnxApiResponse<LbwBrandSettingsModel>> {
        return this._httpClient
            .get<UnxApiResponse<LbwBrandSettingsModel>>('brand-api/settings')
            .pipe(
                tap((r) => {
                    Object.assign(this._settings, r.data);
                    this._settings.widgetRegistry = TRIO_WIDGET_REGISTRY;
                    this._applyAuthGameVisualCss();
                    this._applyFaviconLink();
                }),
            );
    }

    get settings(): LbwBrandSettingsModel {
        return this._settings;
    }

    /**
     * Applies per-language register background and game placeholder URLs to CSS variables.
     * Keeps BRAND_SETTINGS_TOKEN object reference (Object.assign in getBrandSettings).
     */
    private _applyAuthGameVisualCss(): void {
        const html = this._document.documentElement;
        const s = this._settings;
        const lang = this._transloco.getActiveLang();
        const reg = resolveBrandVisualUrl(s.registerBackgroundUrls, s.registerBackgroundUrl, lang);
        const game = resolveBrandVisualUrl(s.gameNoImageUrls, s.gameNoImageUrl, lang);
        if (reg) {
            html.style.setProperty('--register-background', this._cssUrl(reg));
        } else {
            html.style.removeProperty('--register-background');
        }
        if (game) {
            html.style.setProperty('--brand-game-no-image', this._cssUrl(game));
        } else {
            html.style.removeProperty('--brand-game-no-image');
        }
    }

    private _applyFaviconLink(): void {
        const href = this._settings.faviconUrl?.trim();
        if (!href) return;
        const head = this._document.head;
        let link = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (!link) {
            link = this._document.createElement('link');
            link.rel = 'icon';
            head.appendChild(link);
        }
        link.setAttribute('href', href);
    }

    private _cssUrl(url: string): string {
        const escaped = url.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        return `url('${escaped}')`;
    }
}
